import React from 'react';
import { sanitizeHtml, escapeHtml } from '@/utils/sanitize';
import { PositioningMap, type PositioningMapData } from '@/components/positioning/PositioningMap';

interface MarkdownRendererProps {
  content: string;
}

const POSITIONING_MAP_PLACEHOLDER = '___POSITIONING_MAP___';

function extractPositioningMap(text: string): { cleanedText: string; mapData: PositioningMapData | null } {
  const regex = /```positioning_map\s*\n([\s\S]*?)```/;
  const match = text.match(regex);
  if (!match || !match[1]) {
    return { cleanedText: text, mapData: null };
  }

  try {
    const parsed = JSON.parse(match[1].trim());
    if (parsed.axisX && parsed.axisY && Array.isArray(parsed.players) && parsed.players.length >= 2) {
      return {
        cleanedText: text.replace(regex, POSITIONING_MAP_PLACEHOLDER),
        mapData: parsed as PositioningMapData,
      };
    }
  } catch {
    // Invalid JSON — ignore, will be stripped as regular code block
  }

  return { cleanedText: text, mapData: null };
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
  // Extract positioning map data before cleaning code blocks
  const { cleanedText: contentWithMap, mapData } = extractPositioningMap(content);

  // Clean markdown artifacts from AI responses (```html, ```, etc.)
  const cleanContent = contentWithMap
    .replace(/```html\n?/gi, '')
    .replace(/```json\n?/gi, '')
    .replace(/```markdown\n?/gi, '')
    .replace(/```\n?/g, '')
    .replace(/^```$/gm, '')
    .trim();

  // Simple markdown parser for our specific use case
  const renderMarkdown = (text: string) => {
    const lines = text.split('\n');
    const elements: JSX.Element[] = [];
    // Sequential key counter to guarantee unique keys across all element types
    let keyId = 0;
    // Accumulate mixed list blocks (ol + ul items together)
    type ListBlockItem = { type: 'ol' | 'ul'; text: string };
    let listBlock: ListBlockItem[] = [];

    const flushList = () => {
      if (listBlock.length === 0) return;

      const hasOl = listBlock.some(i => i.type === 'ol');

      if (!hasOl) {
        // Pure unordered list
        elements.push(
          <ul
            key={`el-${keyId++}`}
            className="list-disc pl-[24px] space-y-[8px] mb-[16px] font-segoe text-[16px] leading-[24px] text-[#0d0d0d]"
          >
            {listBlock.map((item, idx) => (
              <li key={idx} dangerouslySetInnerHTML={{ __html: formatInline(item.text) }} />
            ))}
          </ul>
        );
      } else {
        // Has ordered items — group: each ol item may have following ul sub-items
        type OlGroup = { text: string; subItems: string[] };
        const groups: OlGroup[] = [];
        const leadingUlItems: string[] = [];

        for (const item of listBlock) {
          if (item.type === 'ol') {
            groups.push({ text: item.text, subItems: [] });
          } else {
            if (groups.length > 0) {
              groups[groups.length - 1]!.subItems.push(item.text);
            } else {
              leadingUlItems.push(item.text);
            }
          }
        }

        // Render leading ul items (before any ol) as standalone <ul>
        if (leadingUlItems.length > 0) {
          elements.push(
            <ul
              key={`el-${keyId++}`}
              className="list-disc pl-[24px] space-y-[8px] mb-[16px] font-segoe text-[16px] leading-[24px] text-[#0d0d0d]"
            >
              {leadingUlItems.map((text, idx) => (
                <li key={idx} dangerouslySetInnerHTML={{ __html: formatInline(text) }} />
              ))}
            </ul>
          );
        }

        // Single numbered item with no sub-items → section heading
        if (groups.length === 1 && groups[0]!.subItems.length === 0) {
          elements.push(
            <h3
              key={`el-${keyId++}`}
              className="font-manrope font-semibold text-[20px] leading-[28px] text-[#0d0d0d] mb-[8px] mt-[16px]"
              dangerouslySetInnerHTML={{ __html: formatInline(groups[0]!.text) }}
            />
          );
        } else {
          // Ordered list with optional nested sub-items
          elements.push(
            <ol
              key={`el-${keyId++}`}
              className="list-decimal pl-[24px] space-y-[8px] mb-[16px] font-segoe text-[16px] leading-[24px] text-[#0d0d0d]"
            >
              {groups.map((group, idx) => (
                <li key={idx}>
                  <span dangerouslySetInnerHTML={{ __html: formatInline(group.text) }} />
                  {group.subItems.length > 0 && (
                    <ul className="list-disc pl-[20px] mt-[4px] space-y-[4px]">
                      {group.subItems.map((sub, subIdx) => (
                        <li key={subIdx} dangerouslySetInnerHTML={{ __html: formatInline(sub) }} />
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ol>
          );
        }
      }

      listBlock = [];
    };

    const formatInline = (text: string) => {
      // ВАЖНО: Сначала экранируем HTML для защиты от XSS
      let escaped = escapeHtml(text);

      // Bold text **text** or __text__
      escaped = escaped.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>');
      escaped = escaped.replace(/__(.*?)__/g, '<strong class="font-semibold">$1</strong>');

      // Italic text *text* or _text_
      escaped = escaped.replace(/\*(.*?)\*/g, '<em class="italic">$1</em>');
      escaped = escaped.replace(/_(.*?)_/g, '<em class="italic">$1</em>');

      // Финальная санитизация результата
      return sanitizeHtml(escaped);
    };

    lines.forEach((line) => {
      const trimmedLine = line.trim();

      // Skip empty lines
      if (!trimmedLine) {
        flushList();
        return;
      }

      // Positioning map placeholder
      if (trimmedLine === POSITIONING_MAP_PLACEHOLDER && mapData) {
        flushList();
        elements.push(<PositioningMap key={`el-${keyId++}`} data={mapData} />);
        return;
      }

      // Headers
      if (trimmedLine.startsWith('#### ')) {
        flushList();
        elements.push(
          <h4
            key={`el-${keyId++}`}
            className="font-manrope font-semibold text-[18px] leading-[26px] text-[#0d0d0d] mb-[8px] mt-[12px]"
            dangerouslySetInnerHTML={{ __html: formatInline(trimmedLine.substring(5)) }}
          />
        );
      } else if (trimmedLine.startsWith('### ')) {
        flushList();
        elements.push(
          <h3
            key={`el-${keyId++}`}
            className="font-manrope font-semibold text-[20px] leading-[28px] text-[#0d0d0d] mb-[8px] mt-[16px]"
            dangerouslySetInnerHTML={{ __html: formatInline(trimmedLine.substring(4)) }}
          />
        );
      } else if (trimmedLine.startsWith('## ')) {
        flushList();
        elements.push(
          <h2
            key={`el-${keyId++}`}
            className="font-manrope font-semibold text-[20px] leading-[28px] text-[#0d0d0d] mb-[12px] mt-[20px] uppercase"
            dangerouslySetInnerHTML={{ __html: formatInline(trimmedLine.substring(3)) }}
          />
        );
      } else if (trimmedLine.startsWith('# ')) {
        flushList();
        elements.push(
          <h1
            key={`el-${keyId++}`}
            className="font-manrope font-bold text-[32px] leading-[40px] text-[#0d0d0d] mb-[16px] mt-[24px]"
            dangerouslySetInnerHTML={{ __html: formatInline(trimmedLine.substring(2)) }}
          />
        );
      }
      // Standalone bold line → render as h3 heading (for existing AI content without markdown headings)
      else if (/^\*\*(.+)\*\*:?$/.test(trimmedLine)) {
        flushList();
        const match = trimmedLine.match(/^\*\*(.+)\*\*:?$/);
        const headingText = match![1]!;
        elements.push(
          <h3
            key={`el-${keyId++}`}
            className="font-manrope font-semibold text-[20px] leading-[28px] text-[#0d0d0d] mb-[8px] mt-[16px]"
            dangerouslySetInnerHTML={{ __html: formatInline(headingText) }}
          />
        );
      }
      // Unordered list item — accumulate into block
      else if (trimmedLine.match(/^[*+-]\s/)) {
        listBlock.push({ type: 'ul', text: trimmedLine.substring(2).replace(/^\[[ x]\] /i, '') });
      }
      // Ordered list item — accumulate into block
      else if (trimmedLine.match(/^\d+\.\s/)) {
        const match = trimmedLine.match(/^\d+\.\s(.+)$/);
        if (match && match[1]) {
          listBlock.push({ type: 'ol', text: match[1].replace(/^\[[ x]\] /i, '') });
        }
      }
      // Horizontal rule
      else if (trimmedLine.match(/^═+$/) || trimmedLine.match(/^-{3,}$/) || trimmedLine.match(/^\*{3,}$/)) {
        flushList();
        elements.push(
          <hr
            key={`el-${keyId++}`}
            className="border-t-2 border-[#eeeeee] my-[24px]"
          />
        );
      }
      // Regular paragraph
      else {
        flushList();
        elements.push(
          <p
            key={`el-${keyId++}`}
            className="font-segoe text-[16px] leading-[24px] text-[#0d0d0d] mb-[12px]"
            dangerouslySetInnerHTML={{ __html: formatInline(trimmedLine) }}
          />
        );
      }
    });

    flushList();
    return elements;
  };

  return <div className="markdown-content">{renderMarkdown(cleanContent)}</div>;
};
